import React, { useState, useEffect } from "react";
import { Class, Level } from "../../../../services/Dtos";
import { VStack, Box } from "@chakra-ui/react";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import CustomAlert from "../../../CustomUi/CustomAlert";
import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import CustomNumberInput from "../../../CustomUi/CustomNumberInput";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useClassService } from "../../../../hooks/Domain/useClassService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import Info from "../../../CustomUi/Info";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  classRoomToEdit: Class | null;
  classLevel: Level | null;
  nextOrder: number;
}

const ClassRoomModal: React.FC<Props> = ({
  isOpen,
  onClose,
  classRoomToEdit,
  classLevel,
  nextOrder,
}) => {
  const {
    createClass,
    updateClass,
    deleteClass,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useClassService();

  const getInitialClassFormState = (): Class => ({
    id: "",
    className: "",
    levelId: classLevel?.id || "",
    subjects: [],
    behavioralSubjects: [],
    order: nextOrder,
    formTeacherId: "",
    schoolId: "",
    createdAt: "",
    updatedAt: "",
  });

  const initialClassFormState = getInitialClassFormState();

  const { levels, users } = useSchoolsContext();
  const icons = useIcons();
  const [classRoomFormState, setClassRoomFormState] = useState<Class>(
    initialClassFormState
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    className: { type: "text" as const, required: true, maxLength: 100 },
    levelId: { type: "text" as const, required: true },
    order: { type: "number" as const, required: true },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: classRoomToEdit,
      currentData: classRoomFormState,
      isEditMode: !!classRoomToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setClassRoomFormState,
    setTouchedFields,
    integerFields: ["order"],
  });

  // Real-time validation
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof Class;
    return validateField(fieldKey, value);
  };

  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (classRoomToEdit) {
      setClassRoomFormState(classRoomToEdit);
    } else {
      setClassRoomFormState(getInitialClassFormState());
    }
    setTouchedFields(new Set());
  }, [classRoomToEdit, classLevel, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      classRoomFormState[fieldName as keyof Class]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      classRoomFormState[fieldName as keyof Class]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (classRoomToEdit) {
      const response = await updateClass(
        classRoomToEdit.id,
        classRoomFormState
      );
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createClass(classRoomFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDelete = async () => {
    if (!classRoomToEdit) return;
    const response = await deleteClass(classRoomToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && classRoomToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const levelOptions = levels.map((level) => ({
    label: level.levelName,
    value: level.id,
  }));

  const teacherOptions = [
    ...users
      .filter((u) => u.role === 1)
      .map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      })),
    { label: "No Teacher", value: "" },
  ];

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDelete}
        deleteMessage="All related class records and student assignments will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={classRoomToEdit ? "Edit Class" : "Add New Class"}
        icon={icons.main.class}
        mode={classRoomToEdit ? "edit" : "add"}
        actionText={classRoomToEdit ? "Save Changes" : "Save Class"}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          <Box>
            <InlineSeparator label="Class Information" />
            <VStack gap={4}>
              <TextInput
                label="Class Name"
                name="className"
                type="text"
                value={classRoomFormState.className}
                onChange={handleFieldChange}
                error={
                  showFieldError("className") ? getFieldError("className") : ""
                }
                invalid={showFieldError("className")}
                required
              />

              <SelectInput
                blankOptionLabel="Select Class Level"
                blankOptionValue={""}
                label="Class Level"
                name="levelId"
                value={classRoomFormState.levelId}
                onChange={handleFieldChange}
                collection={levelOptions}
                placeholder="Select Class Level"
                required
                invalid={showFieldError("levelId")}
                error={
                  showFieldError("levelId") ? getFieldError("levelId") : ""
                }
              />
            </VStack>
          </Box>

         
          <Box>
            <InlineSeparator label="Additional Settings" />
            <VStack gap={4}>
              <SelectInput
                label="Form Teacher"
                name="formTeacherId"
                value={classRoomFormState.formTeacherId}
                onChange={handleFieldChange}
                collection={teacherOptions}
                placeholder="Select Form Teacher"
              />

              <CustomNumberInput
                label="Order"
                value={classRoomFormState.order.toString()}
                onChange={(value) => {
                  setClassRoomFormState((prev) => ({
                    ...prev,
                    order: parseInt(value, 10) || 0,
                  }));
                  setTouchedFields((prev) => new Set(prev).add("order"));
                }}
                min={1}
                error={showFieldError("order") ? getFieldError("order") : ""}
                info={
                  <Info content="The order of the class in a list of classes. This helps with arranging the classes the way you want." />
                }
              />
            </VStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default ClassRoomModal;
